<template>
    <div class="modal modal-todo modal-follow-up fade" tabindex="-1" role="dialog" aria-labelledby="follow-up-modal-label" aria-hidden="true" ref="modal">
        <div class="modal-dialog modal-lg" role="document">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">
                        {{ isUnavailable ? 'Date & Time Unavailable' : 'Follow Up Schedule' }}
                    </h5>
                    <button type="button" class="close" @click="closeModal" aria-label="Close">
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>
                <div class="modal-body">
                    <div v-if="isLoading" class="text-center p-4">
                        <div class="spinner-border text-primary" role="status">
                            <span class="sr-only">Loading...</span>
                        </div>
                    </div>

                    <div v-else-if="isUnavailable" class="text-center px-4 pb-4 pt-2">
                        <font-awesome-icon :icon="['far', 'calendar-exclamation']" style="height:64px; color:#F59E0B;" />
                        <p>Sorry, the selected date or time is not available. Please choose another available option.</p>
                        <button class="btn btn-submit btn-block btn-primary mt-3" @click="reschedule">Choose another schedule</button>
                        <button class="btn btn-action btn-block btn-primary mt-3" @click="justDoIt">
                            <strong>Do it anyway</strong>
                        </button>
                    </div>

                    <div v-else>
                        <div v-if="followUpTypes.length" class="btn-group btn-group-toggle mb-3 d-flex" data-toggle="buttons">
                            <label v-for="fupType in followUpTypes" :key="`${fupType.id}-${fupType.type}`" class="btn btn-outline-primary" :class="{ active: selectedType === fupType.type }" @click="selectedType = fupType.type">
                                <input type="radio" :value="fupType.type" :checked="selectedType === fupType.type" />
                                <font-awesome-icon :icon="getIconClass(fupType.type)" class="mr-2" />
                                {{ fupType.translated_name }}
                            </label>
                        </div>

                        <form @submit.prevent="saveFollowUpDate">
                            <div v-if="['office', 'voice_call', 'video_call'].includes(selectedType)">
                                <div class="row">
                                    <div class="col-md-5">
                                        <div class="form-group">
                                            <label for="client-timezone">Client Timezone <span v-if="requiresDuration" class="text-danger">*</span></label>
                                            <v-select id="client-timezone" :options="timezones" v-model="selectedTimezone" label="label" :reduce="tz => tz.value" placeholder="Select timezone" required />
                                            <div class="invalid-feedback d-block" v-if="requiresDuration && !selectedTimezone">
                                                Timezone is required.
                                            </div>
                                        </div>
                                    </div>

                                    <div class="col-md-7">
                                        <datetime-picker id="follow-up-local" v-model="localDateTime">
                                            <template #label>
                                                <span>Your Local Date and Time</span>
                                                <span v-if="userTimezone" class="text-muted font-weight-normal"> ({{ userTimezone }})</span>
                                            </template>
                                        </datetime-picker>
                                    </div>
                                </div>

                                <div v-if="localDateTime && selectedTimezone" class="mt-3">
                                    <div class="row">
                                        <div class="col-md-5">
                                            <div class="form-group">
                                                <label for="duration">Duration <span v-if="requiresDuration" class="text-danger">*</span></label>
                                                <v-select id="duration" :options="durations" v-model="selectedDuration" label="label" :reduce="d => d.value" placeholder="Select duration" required />
                                                <div class="invalid-feedback d-block" v-if="requiresDuration && !selectedDuration">
                                                    Duration is required.
                                                </div>
                                            </div>
                                        </div>

                                        <div class="col-md-7">
                                            <div class="form-group">
                                                <label for="local-time">
                                                    Time in {{ selectedTimezone || 'client timezone' }}
                                                </label>
                                                <input id="local-time" type="text" class="form-control" :value="formattedClientTime" disabled />
                                            </div>
                                        </div>

                                        <div class="col-md-12">
                                            <div class="form-group">
                                                <label for="follow-up-comments">Comments</label>
                                                <textarea id="follow-up-comments" class="form-control" v-model="followUpComments" rows="4" placeholder="Type here..." :required="selectedType === 'reminder'"></textarea>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div v-if="selectedType === 'reminder'">
                                <datetime-picker id="follow-up-local" label="Date" v-model="localDateTime" />
                                <div class="form-group">
                                    <label for="follow-up-comments">Comments<span class="text-danger">*</span></label>
                                    <textarea id="follow-up-comments" class="form-control" v-model="followUpComments" rows="4" placeholder="Type here..." :required="selectedType === 'reminder'"></textarea>
                                </div>
                            </div>

                            <button type="submit" class="btn btn-submit btn-block mt-3 btn-primary":disabled="requiresDuration && !selectedDuration">
                                Save
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import vSelect from 'vue-select';
import moment from 'moment-timezone';
import DatetimePicker from '../../../components/form/DatetimePicker.vue';
import { getFollowUpTypes } from '../../../services/api/to-do';
import { getDurationOptions } from '../../../utils/helpers';

export default {
    name: 'FollowUpModal',
    components: { DatetimePicker, vSelect },
    data() {
        return {
            todoId: 0,
            userTimezone: null,
            followUpTypes: [],
            followUpComments: '',
            isLoading: false,
            isUnavailable: false,
            selectedType: 'reminder',
            selectedTimezone: null,
            localDateTime: '',
            selectedDuration: null,
            timezones: moment.tz.names().map(tz => ({ label: tz, value: tz })),
            durations: getDurationOptions()
        }
    },
    mounted() {
        document.body.appendChild(this.$el);
    },
    computed: {
        requiresDuration() {
            return ['visit', 'voice_call', 'video_call'].includes(this.selectedType);
        },
        formattedClientTime() {
            if (!this.localDateTime || !this.selectedTimezone || !this.userTimezone) {
                return '';
            }

            // First parse the date in the user's timezone, then convert to the selected timezone
            return moment.tz(this.localDateTime, this.userTimezone)
                .tz(this.selectedTimezone)
                .format('MMMM DD, YYYY hh:mm A');
        }
    },
    methods: {
        openModal() {
            this.followUpComments = null;
            this.isUnavailable = false;
            this.isLoading = false;
            this.selectedType = 'reminder';

            this.fetchFollowUpTypes();

            this.$nextTick(() => {
                $(this.$refs.modal).modal({
                    backdrop: 'static',
                    keyboard: false
                });
            })
        },
        closeModal() {
            $(this.$refs.modal).modal('hide');
        },
        async fetchFollowUpTypes() {
            try {
                const response = await getFollowUpTypes(this.todoId);
                const { types, user_timezone } = response.data;

                types.push({
                    id: 'reminder',
                    type: 'reminder',
                    translated_name: 'Reminder',
                    translated_description: 'Set a personal reminder for yourself',
                    category: 'personal'
                });

                this.followUpTypes = types;
                this.userTimezone = user_timezone;
            } catch (error) {
                console.error(`Error fetching todo items`, error);
                this.followUpTypes = [];
                this.userTimezone = null;
            }
        },
        saveFollowUpDate(forceSave = false) {
            if (['visit', 'voice_call', 'video_call'].includes(this.selectedType)) {
                if (!this.selectedTimezone) {
                    alert('Client timezone is required.');
                    return;
                }

                if (!this.localDateTime || !moment(this.localDateTime).isValid()) {
                    alert('Local date and time is required and must be valid.');
                    return;
                }

                if (!this.selectedDuration) {
                    alert('Duration is required.');
                    return;
                }
            }

            if (this.selectedType === 'reminder' && !this.followUpComments?.trim()) {
                alert('Comments are required for reminders.');
                return;
            }

            const payload = {
                type: this.selectedType,
                client_date: this.userTimezone && this.selectedTimezone 
                    ? moment.tz(this.localDateTime, this.userTimezone).tz(this.selectedTimezone).format('YYYY-MM-DD HH:mm:ss')
                    : moment(this.localDateTime).format('YYYY-MM-DD HH:mm:ss'),
                user_date: moment(this.localDateTime).format('YYYY-MM-DD HH:mm:ss'),
                timezone: this.selectedTimezone,
                duration: this.selectedDuration,
                comments: this.followUpComments,
                force: forceSave,
            };

            if (this.requiresDuration && !this.selectedDuration) {
                return;
            }

            this.isLoading = true;
            this.$emit('save', { payload, callback: this.saveResponse });
        },
        saveResponse(response) {
            this.isLoading = false;

            if (response.status === 'unavailable') {
                this.isUnavailable = true;
            } else {
                this.closeModal();
            }
        },
        reschedule() {
            this.isUnavailable = false;
        },
        justDoIt() {
            this.isLoading = true;
            this.saveFollowUpDate(true);
        },
        getIconClass(type) {
            const iconMap = {
                voice_call: ['fas', 'phone'],
                video_call: ['fas', 'video'],
                office: ['fas', 'building'],
                reminder: ['fas', 'bell']
            };
            return iconMap[type] || ['fas', 'question'];
        },
    }
}
</script>
